import { Injectable, Optional } from '@angular/core';
import { environment } from 'src/environments/environment';
import { TelemetryService } from './telemetry.service';

/**
 * Centralized, environment-aware logging service.
 * In production (`environment.production === true`), log, info, and warn
 * calls are suppressed to keep the production browser console clean.
 * Errors passed to `error()` are captured and forwarded to Application Insights via TelemetryService.
 */
@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private static telemetryService?: TelemetryService;

  constructor(@Optional() telemetryService?: TelemetryService) {
    if (telemetryService) {
      LoggingService.telemetryService = telemetryService;
    }
  }

  static setTelemetryService(service: TelemetryService): void {
    LoggingService.telemetryService = service;
  }

  static log(...args: unknown[]): void {
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  }

  static info(...args: unknown[]): void {
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.info(...args);
    }
  }

  static warn(...args: unknown[]): void {
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.warn(...args);
    }
  }

  static error(...args: unknown[]): void {
    if (!environment.production) {
      // eslint-disable-next-line no-console
      console.error(...args);
    }

    if (LoggingService.telemetryService) {
      const firstArg = args[0];
      const errorObj =
        firstArg instanceof Error
          ? firstArg
          : new Error(
              args
                .map((a) => {
                  if (a instanceof Error) return a.message;
                  if (typeof a === 'object') {
                    try {
                      return JSON.stringify(a);
                    } catch {
                      return String(a);
                    }
                  }
                  return String(a);
                })
                .join(' '),
            );
      LoggingService.telemetryService.logException(errorObj);
    }
  }

  log(...args: unknown[]): void {
    LoggingService.log(...args);
  }

  info(...args: unknown[]): void {
    LoggingService.info(...args);
  }

  warn(...args: unknown[]): void {
    LoggingService.warn(...args);
  }

  error(...args: unknown[]): void {
    LoggingService.error(...args);
  }
}
